if (!customElements.get('v12-finance-calculator')) {
  customElements.define(
    'v12-finance-calculator',
    class V12FinanceCalculator extends HTMLElement {
      connectedCallback() {
        this.productSelect = this.querySelector('[data-v12-product-select]');
        this.depositRange = this.querySelector('[data-v12-deposit-range]');
        this.depositPresetButtons = Array.from(this.querySelectorAll('[data-v12-deposit-preset]'));
        this.sliderTooltips = this.querySelectorAll('[data-v12-slider-tooltip]');
        this.notice = this.querySelector('[data-v12-notice]');
        this.textTargets = {
          monthlyPayment: this.querySelectorAll('[data-v12-monthly-payment]'),
          deposit: this.querySelectorAll('[data-v12-deposit]'),
          depositPercent: this.querySelectorAll('[data-v12-deposit-percent]'),
          loan: this.querySelectorAll('[data-v12-loan]'),
          cashPrice: this.querySelectorAll('[data-v12-cash-price]'),
          minDeposit: this.querySelectorAll('[data-v12-min-deposit]'),
          maxDeposit: this.querySelectorAll('[data-v12-max-deposit]'),
          term: this.querySelectorAll('[data-v12-term]'),
          apr: this.querySelectorAll('[data-v12-apr]'),
          annualRate: this.querySelectorAll('[data-v12-annual-rate]'),
          totalPayable: this.querySelectorAll('[data-v12-total-payable]'),
          costCredit: this.querySelectorAll('[data-v12-cost-credit]'),
        };
        this.onInputChange = this.onInputChange.bind(this);
        this.onDepositPresetClick = this.onDepositPresetClick.bind(this);

        this.productSelect?.addEventListener('change', this.onInputChange);
        this.depositRange?.addEventListener('input', this.onInputChange);
        this.depositPresetButtons.forEach((button) => button.addEventListener('click', this.onDepositPresetClick));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.sectionId || !data.variant) return;
            this.setPriceCents(data.variant.price);
            this.render();
          });
        }

        this.render();
      }

      disconnectedCallback() {
        this.productSelect?.removeEventListener('change', this.onInputChange);
        this.depositRange?.removeEventListener('input', this.onInputChange);
        this.depositPresetButtons.forEach((button) => button.removeEventListener('click', this.onDepositPresetClick));
        this.variantChangeUnsubscriber?.();
      }

      onInputChange() {
        this.render();
      }

      onDepositPresetClick(event) {
        const preset = Number(event.currentTarget.dataset.v12DepositPreset);

        if (!Number.isFinite(preset)) return;

        this.setDepositPercent(preset);
        this.render();
      }

      render() {
        if (!this.isEligible) {
          this.hidden = true;
          return;
        }

        this.hidden = false;

        const price = this.productPrice;
        const financeProduct = this.selectedFinanceProduct;
        const depositPercent = this.clampedDepositPercent;
        const result = this.calculateFinance(financeProduct, price, depositPercent);
        const { textTargets } = this;

        this.setText(textTargets.monthlyPayment, this.formatMoney(result.monthlyPayment));
        this.setText(textTargets.deposit, this.formatMoney(result.deposit));
        this.setText(textTargets.depositPercent, this.formatPercentNumber(depositPercent));
        this.setText(textTargets.loan, this.formatMoney(result.loan));
        this.setText(textTargets.cashPrice, this.formatMoney(price));
        this.setText(textTargets.minDeposit, this.formatMoney(price * (this.minDepositPercent / 100)));
        this.setText(textTargets.maxDeposit, this.formatMoney(price * (this.maxDepositPercent / 100)));
        this.setText(textTargets.term, financeProduct.months);
        this.setText(textTargets.apr, this.formatPercent(financeProduct.apr));
        this.setText(textTargets.annualRate, this.formatPercent(financeProduct.annualRate));
        this.setText(textTargets.totalPayable, this.formatMoney(result.totalPayable));
        this.setText(textTargets.costCredit, this.formatMoney(result.costOfCredit));

        this.updateDepositPresetButtons(depositPercent);
        this.updateSliderTooltip(result.deposit, depositPercent);
        this.updateNotice(result.loan, financeProduct);
      }

      updateSliderTooltip(deposit, depositPercent) {
        if (!this.sliderTooltips.length) return;

        const range = this.maxDepositPercent - this.minDepositPercent;
        const ratio = range > 0 ? (depositPercent - this.minDepositPercent) / range : 0;

        this.setText(this.sliderTooltips, this.formatMoneyRounded(deposit));
        this.style.setProperty('--v12-slider-ratio', ratio);
      }

      calculateFinance(financeProduct, price, depositPercent) {
        const deposit = price * (depositPercent / 100);
        const loan = Math.max(price - deposit, 0);
        const fees = financeProduct.documentFee + financeProduct.serviceFee;
        let monthlyPayment;
        let totalPayable;

        if (financeProduct.apr <= 0 && fees <= 0) {
          monthlyPayment = financeProduct.months > 0 ? loan / financeProduct.months : 0;
          totalPayable = price;
        } else {
          const factor = financeProduct.factor > 0 ? financeProduct.factor : this.factorFromApr(financeProduct.apr, financeProduct.months);
          monthlyPayment = loan * factor;
          totalPayable = deposit + monthlyPayment * financeProduct.months + fees;
        }

        const costOfCredit = Math.max(totalPayable - price, 0);

        return {
          deposit,
          loan,
          monthlyPayment,
          totalPayable,
          costOfCredit,
          fees,
        };
      }

      factorFromApr(apr, months) {
        if (!months) return 0;

        const monthlyRate = Number(apr) / 100 / 12;

        if (!monthlyRate) return 1 / months;

        return monthlyRate / (1 - Math.pow(1 + monthlyRate, -months));
      }

      updateDepositPresetButtons(depositPercent) {
        this.depositPresetButtons.forEach((button) => {
          const preset = Number(button.dataset.v12DepositPreset);
          const isActive = preset === depositPercent;

          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      updateNotice(loan, financeProduct) {
        if (!this.notice) return;

        let message = '';

        if (financeProduct.minLoan > 0 && loan < financeProduct.minLoan) {
          message = `Minimum amount of credit for this option is ${this.formatMoney(financeProduct.minLoan)}.`;
        } else if (financeProduct.maxLoan > 0 && loan > financeProduct.maxLoan) {
          message = `Maximum amount of credit for this option is ${this.formatMoney(financeProduct.maxLoan)}.`;
        }

        this.notice.textContent = message;
        this.notice.toggleAttribute('hidden', !message);
        this.classList.toggle('v12-finance-calculator--invalid', Boolean(message));
      }

      setPriceCents(value) {
        this.dataset.productPrice = Math.max(Math.round(Number(value) || 0), 0);
      }

      setDepositPercent(value) {
        const nextValue = this.clampDepositPercent(value);

        if (this.depositRange) {
          this.depositRange.value = nextValue;
        }

        return nextValue;
      }

      clampDepositPercent(value) {
        const rawValue = Number(value);
        return Math.min(Math.max(Number.isFinite(rawValue) ? rawValue : this.minDepositPercent, this.minDepositPercent), this.maxDepositPercent);
      }

      setText(nodeList, value) {
        nodeList.forEach((element) => {
          element.textContent = value;
        });
      }

      formatMoney(value) {
        return new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'GBP',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number.isFinite(value) ? value : 0);
      }

      formatMoneyRounded(value) {
        return new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'GBP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(Number.isFinite(value) ? value : 0);
      }

      formatPercent(value) {
        const percent = Number(value);

        if (!Number.isFinite(percent)) return '0%';

        return `${percent.toFixed(percent % 1 === 0 ? 0 : 2)}%`;
      }

      formatPercentNumber(value) {
        const percent = Number(value);

        if (!Number.isFinite(percent)) return '0';

        return percent.toFixed(percent % 1 === 0 ? 0 : 2);
      }

      financeProductFromDataset(dataset) {
        const apr = Number(dataset.apr || 0);
        const annualRate = Number(dataset.annualRate || 0);

        return {
          tag: dataset.tag || '',
          months: Number(dataset.months || 1),
          apr,
          factor: Number(dataset.factor || 0),
          minLoan: Number(dataset.minLoan || 0),
          maxLoan: Number(dataset.maxLoan || 0),
          documentFee: Number(dataset.documentFee || 0),
          serviceFee: Number(dataset.serviceFee || 0),
          annualRate: annualRate > 0 ? annualRate : apr,
        };
      }

      get productPrice() {
        return this.productPriceCents / 100;
      }

      get productPriceCents() {
        return Number(this.dataset.productPrice || 0);
      }

      get minPriceCents() {
        return Number(this.dataset.minPrice || 0);
      }

      get maxPriceCents() {
        return Number(this.dataset.maxPrice || Number.MAX_SAFE_INTEGER);
      }

      get minDepositPercent() {
        return Number(this.dataset.minDeposit || 0);
      }

      get maxDepositPercent() {
        return Number(this.dataset.maxDeposit || 100);
      }

      get priceWithinFinanceRange() {
        return this.productPriceCents >= this.minPriceCents && this.productPriceCents <= this.maxPriceCents;
      }

      get isEligible() {
        return this.dataset.disabledByTag !== 'true' && this.priceWithinFinanceRange;
      }

      get clampedDepositPercent() {
        return this.setDepositPercent(this.depositRange?.value || this.dataset.defaultDeposit || this.minDepositPercent);
      }

      get selectedFinanceProduct() {
        const option = this.productSelect?.selectedOptions?.[0] || this.productSelect?.options?.[0];

        return this.financeProductFromDataset(option?.dataset || {});
      }
    }
  );
}
